"use client";

import { AlertTriangle, CheckCircle2, CircleOff, Clock3, ShieldAlert, WifiOff } from "lucide-react";
import { presentationForState } from "@/lib/macro";
import type { TruthState } from "@/lib/types";
import { useOnlineStatus } from "@/lib/use-online-status";

type DataStateProps = {
  status: TruthState;
  source?: string;
  observationDate?: string | null;
  lastSuccessAt?: string | null;
  online?: boolean;
  compact?: boolean;
};

const ICONS = {
  LIVE: CheckCircle2,
  DELAYED: Clock3,
  STALE: AlertTriangle,
  UNAVAILABLE: CircleOff,
  QUARANTINED: ShieldAlert,
  OFFLINE: WifiOff
};

const CLASSES = {
  positive: "border-[var(--hc-positive)]/40 bg-[var(--hc-positive-soft)] text-[var(--hc-positive)]",
  warning: "border-[var(--hc-warning)]/40 bg-[var(--hc-warning-soft)] text-[var(--hc-warning)]",
  neutral: "border-[var(--hc-border-strong)] bg-[var(--hc-surface-2)] text-[var(--hc-muted)]",
  danger: "border-[var(--hc-negative)]/40 bg-[var(--hc-negative-soft)] text-[var(--hc-negative)]"
};

function formatObservation(value?: string | null) {
  if (!value) return null;
  const date = new Date(`${value.slice(0, 10)}T00:00:00Z`);
  if (Number.isNaN(date.valueOf())) return value;
  return new Intl.DateTimeFormat("en", { month: "short", year: "numeric", timeZone: "UTC" }).format(date).toUpperCase();
}

function formatVerified(value?: string | null) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.valueOf())) return value;
  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
    timeZone: "UTC"
  }).format(date).toUpperCase();
}

export function DataState({ status, source, observationDate, lastSuccessAt, online = true, compact = false }: DataStateProps) {
  const detectedOnline = useOnlineStatus();
  const presentation = presentationForState(status, online && detectedOnline);
  const Icon = ICONS[presentation.label];
  const observed = formatObservation(observationDate);
  const verified = formatVerified(lastSuccessAt);
  const detail = presentation.label === "LIVE" && observed
    ? `${source ?? "Validated source"} · OBS ${observed}`
    : presentation.label === "STALE" || presentation.label === "OFFLINE"
      ? verified ? `last verified ${verified} UTC` : presentation.description
      : presentation.description;

  return (
    <span
      className={`inline-flex max-w-full items-center gap-1.5 rounded border px-2 py-1 text-[11px] font-semibold ${CLASSES[presentation.tone]}`}
      title={detail}
    >
      <Icon size={13} strokeWidth={2} aria-hidden="true" />
      <span>{presentation.label}</span>
      {compact ? null : <span className="truncate font-medium opacity-85">· {detail}</span>}
    </span>
  );
}
